import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, Brain, Sparkles, Zap } from 'lucide-react';
import { AI_CAPABILITIES } from '../config/constants';

const AnimatedSection = ({ children, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* ── Inline SVG visuals for each AI capability ──────── */
const AIVisual = ({ index, color }) => {
  const visuals = [
    // Card to Lead - Business card scanning
    (
      <svg viewBox="0 0 220 150" className="w-full h-full">
        <defs>
          <linearGradient id="cardG" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="0.7" />
            <stop offset="100%" stopColor={color} stopOpacity="0.3" />
          </linearGradient>
        </defs>
        {/* Business card shape */}
        <motion.rect x="30" y="30" width="100" height="60" rx="6" fill="none" stroke={color} strokeWidth="1.5"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ duration: 0.8 }} />
        {/* Card content lines */}
        <motion.rect x="45" y="42" width="35" height="3" rx="1" fill={color} fillOpacity="0.3"
          initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ delay: 0.5 }} style={{ transformOrigin: 'left' }} />
        <motion.rect x="45" y="50" width="50" height="2" rx="1" fill={color} fillOpacity="0.2"
          initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ delay: 0.6 }} style={{ transformOrigin: 'left' }} />
        <motion.rect x="45" y="57" width="40" height="2" rx="1" fill={color} fillOpacity="0.2"
          initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ delay: 0.7 }} style={{ transformOrigin: 'left' }} />
        <motion.rect x="45" y="64" width="55" height="2" rx="1" fill={color} fillOpacity="0.15"
          initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ delay: 0.8 }} style={{ transformOrigin: 'left' }} />
        {/* Scan line */}
        <motion.line x1="30" y1="60" x2="130" y2="60" stroke={color} strokeWidth="2" strokeOpacity="0.5"
          animate={{ y1: [35, 85, 35], y2: [35, 85, 35] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }} />
        {/* Arrow to output */}
        <motion.path d="M140 60 L160 60" stroke={color} strokeWidth="1.5" markerEnd="url(#arrowAI)"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ delay: 1 }} />
        {/* Output - CRM entry */}
        <motion.rect x="165" y="40" width="45" height="40" rx="4" fill={color} fillOpacity="0.08"
          stroke={color} strokeWidth="1"
          initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ delay: 1.2 }} />
        <motion.text x="187" y="55" fill={color} fontSize="6" textAnchor="middle" fillOpacity="0.6">Lead</motion.text>
        <motion.text x="187" y="65" fill={color} fontSize="6" textAnchor="middle" fillOpacity="0.6">Created</motion.text>
        <motion.text x="110" y="120" fill="rgba(255,255,255,0.2)" fontSize="7" textAnchor="middle">Business Card to CRM Lead</motion.text>
      </svg>
    ),
    // Drawing to BOM - 2D engineering drawing
    (
      <svg viewBox="0 0 220 150" className="w-full h-full">
        <defs>
          <linearGradient id="drawG" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="0.7" />
            <stop offset="100%" stopColor={color} stopOpacity="0.3" />
          </linearGradient>
        </defs>
        {/* 2D Engineering Drawing */}
        <motion.rect x="20" y="25" width="80" height="65" rx="2" fill="none" stroke={color} strokeWidth="1.5"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ duration: 0.8 }} />
        {/* Drawing inner details - shaft */}
        <motion.rect x="30" y="45" width="60" height="15" fill="none" stroke={color} strokeWidth="1" strokeOpacity="0.6"
          initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ delay: 0.4 }} style={{ transformOrigin: 'left' }} />
        {/* Dimension lines */}
        <motion.line x1="30" y1="70" x2="90" y2="70" stroke={color} strokeWidth="0.6" strokeOpacity="0.4"
          initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ delay: 0.6 }} style={{ transformOrigin: 'left' }} />
        <motion.text x="60" y="78" fill={color} fontSize="5" textAnchor="middle" fillOpacity="0.4">150mm</motion.text>
        {/* Circles for holes */}
        <motion.circle cx="40" cy="52" r="5" fill="none" stroke={color} strokeWidth="0.8" strokeOpacity="0.5"
          initial={{ scale: 0 }} whileInView={{ scale: 1 }} transition={{ delay: 0.5 }} />
        <motion.circle cx="80" cy="52" r="5" fill="none" stroke={color} strokeWidth="0.8" strokeOpacity="0.5"
          initial={{ scale: 0 }} whileInView={{ scale: 1 }} transition={{ delay: 0.55 }} />
        {/* AI processing arrow */}
        <motion.path d="M110 57 L130 57" stroke={color} strokeWidth="1.5"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ delay: 0.8 }} />
        <motion.circle cx="120" cy="57" r="10" fill={color} fillOpacity="0.1" stroke={color} strokeWidth="1"
          initial={{ scale: 0 }} whileInView={{ scale: 1 }} transition={{ delay: 0.9 }} />
        <motion.text x="120" y="60" fill={color} fontSize="7" textAnchor="middle" fillOpacity="0.6">AI</motion.text>
        {/* BOM Output table */}
        <motion.rect x="140" y="30" width="65" height="55" rx="3" fill={color} fillOpacity="0.05"
          stroke={color} strokeWidth="1"
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 1.1 }} />
        {/* BOM rows */}
        {[38, 48, 58, 68].map((y, i) => (
          <motion.rect key={i} x="145" y={y} width={[50, 45, 55, 40][i]} height="2" rx="1"
            fill={color} fillOpacity={0.3 - i * 0.05}
            initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ delay: 1.3 + i * 0.1 }} style={{ transformOrigin: 'left' }} />
        ))}
        <motion.text x="110" y="120" fill="rgba(255,255,255,0.2)" fontSize="7" textAnchor="middle">2D Drawing to BOM Extraction</motion.text>
      </svg>
    ),
    // Notes to Minutes
    (
      <svg viewBox="0 0 220 150" className="w-full h-full">
        <defs>
          <linearGradient id="notesG" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="0.6" />
            <stop offset="100%" stopColor={color} stopOpacity="0.3" />
          </linearGradient>
        </defs>
        {/* Sound waves */}
        {[0, 1, 2].map((i) => (
          <motion.path key={i}
            d={`M${30 + i * 12} 50 Q${36 + i * 12} 35 ${42 + i * 12} 50 Q${48 + i * 12} 65 ${54 + i * 12} 50`}
            fill="none" stroke={color} strokeWidth="1.2" strokeOpacity={0.6 - i * 0.15}
            initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ delay: i * 0.2, duration: 0.5 }} />
        ))}
        {/* Microphone icon */}
        <motion.rect x="20" y="60" width="16" height="25" rx="8" fill="none" stroke={color} strokeWidth="1.5"
          initial={{ scale: 0 }} whileInView={{ scale: 1 }} transition={{ delay: 0.3 }} />
        <motion.line x1="28" y1="85" x2="28" y2="95" stroke={color} strokeWidth="1.5"
          initial={{ scaleY: 0 }} whileInView={{ scaleY: 1 }} transition={{ delay: 0.5 }} style={{ transformOrigin: 'top' }} />
        {/* Arrow */}
        <motion.path d="M70 65 L90 65" stroke={color} strokeWidth="1.5"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ delay: 0.7 }} />
        {/* Document output */}
        <motion.rect x="100" y="30" width="100" height="80" rx="4" fill={color} fillOpacity="0.05"
          stroke={color} strokeWidth="1"
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 0.9 }} />
        {/* Minutes content */}
        <motion.text x="110" y="48" fill={color} fontSize="6" fillOpacity="0.5">Meeting Minutes</motion.text>
        <motion.line x1="110" y1="52" x2="185" y2="52" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" />
        {['Action: Review BOM', 'Owner: John D.', 'Due: Feb 15', 'Status: Pending'].map((text, i) => (
          <motion.text key={i} x="115" y={63 + i * 12} fill="rgba(255,255,255,0.3)" fontSize="5.5"
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 1.1 + i * 0.1 }}>
            {text}
          </motion.text>
        ))}
        <motion.text x="110" y="130" fill="rgba(255,255,255,0.2)" fontSize="7" textAnchor="middle">Speech to Structured Minutes</motion.text>
      </svg>
    ),
    // Smart Analytics
    (
      <svg viewBox="0 0 220 150" className="w-full h-full">
        <defs>
          <linearGradient id="anlG" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={color} stopOpacity="0.5" />
            <stop offset="100%" stopColor={color} stopOpacity="0.2" />
          </linearGradient>
        </defs>
        {/* Prediction curve */}
        <motion.path d="M30 100 Q60 90 80 70 Q100 50 130 55 Q160 60 180 35"
          fill="none" stroke={color} strokeWidth="2"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ duration: 1.2 }} />
        {/* Data points */}
        {[[30,100],[60,85],[80,70],[110,55],[140,58],[170,40]].map(([x,y], i) => (
          <motion.circle key={i} cx={x} cy={y} r="3" fill={color} fillOpacity="0.4" stroke={color} strokeWidth="1"
            initial={{ scale: 0 }} whileInView={{ scale: 1 }} transition={{ delay: 0.8 + i * 0.1 }} />
        ))}
        {/* Confidence band */}
        <motion.path d="M130 45 Q160 50 180 25" fill="none" stroke={color} strokeWidth="1" strokeDasharray="3 2" strokeOpacity="0.3"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ delay: 1.2 }} />
        <motion.path d="M130 65 Q160 70 180 45" fill="none" stroke={color} strokeWidth="1" strokeDasharray="3 2" strokeOpacity="0.3"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ delay: 1.3 }} />
        {/* Axis */}
        <motion.line x1="25" y1="110" x2="200" y2="110" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" />
        <motion.line x1="25" y1="25" x2="25" y2="110" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" />
        {/* Label */}
        <motion.text x="190" y="30" fill={color} fontSize="6" fillOpacity="0.5">Predicted</motion.text>
        <motion.text x="110" y="130" fill="rgba(255,255,255,0.2)" fontSize="7" textAnchor="middle">Predictive Win/Loss Analysis</motion.text>
      </svg>
    ),
    // Risk Intelligence
    (
      <svg viewBox="0 0 220 150" className="w-full h-full">
        <defs>
          <linearGradient id="riskG" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="0.6" />
            <stop offset="100%" stopColor={color} stopOpacity="0.2" />
          </linearGradient>
        </defs>
        {/* Risk gauge */}
        <motion.path d="M60 95 A50 50 0 0 1 160 95" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8"
          strokeLinecap="round" />
        <motion.path d="M60 95 A50 50 0 0 1 160 95" fill="none" stroke="url(#riskG)" strokeWidth="8"
          strokeLinecap="round" strokeDasharray="157"
          initial={{ strokeDashoffset: 157 }} whileInView={{ strokeDashoffset: 50 }}
          transition={{ duration: 1.5, ease: 'easeOut' }} />
        {/* Needle */}
        <motion.line x1="110" y1="95" x2="130" y2="60" stroke={color} strokeWidth="2" strokeLinecap="round"
          initial={{ rotate: -90 }} whileInView={{ rotate: 20 }}
          transition={{ delay: 0.5, duration: 1, ease: 'easeOut' }}
          style={{ transformOrigin: '110px 95px' }} />
        <motion.circle cx="110" cy="95" r="4" fill={color} />
        {/* Labels */}
        <motion.text x="55" y="108" fill="rgba(20,184,166,0.5)" fontSize="6">Low</motion.text>
        <motion.text x="105" y="40" fill="rgba(245,158,11,0.5)" fontSize="6">Med</motion.text>
        <motion.text x="155" y="108" fill="rgba(239,68,68,0.5)" fontSize="6">High</motion.text>
        <motion.text x="110" y="130" fill="rgba(255,255,255,0.2)" fontSize="7" textAnchor="middle">Automated Risk Scoring</motion.text>
      </svg>
    ),
    // Smart Reports
    (
      <svg viewBox="0 0 220 150" className="w-full h-full">
        <defs>
          <linearGradient id="repG" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="0.6" />
            <stop offset="100%" stopColor={color} stopOpacity="0.3" />
          </linearGradient>
        </defs>
        {/* Chat bubble - NL query */}
        <motion.rect x="20" y="30" width="80" height="30" rx="8" fill={color} fillOpacity="0.08"
          stroke={color} strokeWidth="1"
          initial={{ scale: 0 }} whileInView={{ scale: 1 }} transition={{ duration: 0.4 }} />
        <motion.text x="60" y="48" fill={color} fontSize="6" textAnchor="middle" fillOpacity="0.6">Show Q4 trends</motion.text>
        {/* Arrow */}
        <motion.path d="M60 65 L60 75" stroke={color} strokeWidth="1" strokeDasharray="2 2"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ delay: 0.5 }} />
        {/* Report output */}
        <motion.rect x="25" y="80" width="170" height="55" rx="6" fill={color} fillOpacity="0.05"
          stroke={color} strokeWidth="1"
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 0.7 }} />
        {/* Mini chart in report */}
        <motion.path d="M40 115 L60 105 L80 110 L100 95 L120 100 L140 90 L160 85"
          fill="none" stroke={color} strokeWidth="1.5"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ delay: 1, duration: 0.8 }} />
        {/* Dots */}
        {[[40,115],[80,110],[120,100],[160,85]].map(([x,y], i) => (
          <motion.circle key={i} cx={x} cy={y} r="2" fill={color}
            initial={{ scale: 0 }} whileInView={{ scale: 1 }} transition={{ delay: 1.5 + i * 0.1 }} />
        ))}
        <motion.text x="100" y="92" fill={color} fontSize="5.5" textAnchor="middle" fillOpacity="0.5">AI-Generated Report</motion.text>
        <motion.text x="110" y="145" fill="rgba(255,255,255,0.2)" fontSize="7" textAnchor="middle">Natural Language to Insight</motion.text>
      </svg>
    ),
  ];
  return visuals[index] || null;
};

export const AICapabilitiesPage = () => {
  return (
    <div className="pt-24" data-testid="ai-capabilities-page">
      {/* Hero */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px]">
          <div className="absolute inset-0 bg-gradient-to-r from-[#00D4FF]/10 via-[#3B82F6]/10 to-[#8B5CF6]/10 rounded-full blur-[150px] animate-pulse" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-[#00D4FF]/10 border border-[#00D4FF]/20 text-[#00D4FF] text-sm font-medium"
              data-testid="ai-hero-badge">
              <Brain className="w-4 h-4" /> AI-Powered Intelligence
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Intelligent Automation<br />
              <span className="text-[#00D4FF]">for Manufacturing</span>
            </h1>
            <p className="text-white/50 text-base md:text-lg max-w-2xl mx-auto mb-10">
              Harness the power of AI to automate data extraction, gain predictive insights,
              and make smarter decisions across your quotation workflow.
            </p>
            {/* AI pulse animation */}
            <div className="max-w-sm mx-auto glass-card p-5">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-10 h-10 rounded-full bg-[#00D4FF]/20 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-[#00D4FF]" />
                </div>
                <div className="flex-1">
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <motion.div className="h-full bg-gradient-to-r from-[#00D4FF] to-[#3B82F6]"
                      initial={{ width: '0%' }} animate={{ width: '100%' }}
                      transition={{ duration: 2, repeat: Infinity, repeatType: 'reverse' }} />
                  </div>
                </div>
              </div>
              <p className="text-white/40 text-xs text-center">AI processing your data in real-time</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* AI Capability Stories */}
      {AI_CAPABILITIES.map((cap, idx) => {
        const isReversed = idx % 2 === 1;
        return (
          <section key={cap.title}
            className={`py-20 md:py-28 ${idx % 2 === 1 ? 'bg-white/[0.01]' : ''}`}
            data-testid={`ai-capability-${idx}`}>
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                {/* Visual */}
                <AnimatedSection className={isReversed ? 'lg:order-2' : ''}>
                  <div className="relative aspect-[3/2] max-w-lg mx-auto">
                    <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/[0.03] to-transparent border border-white/5 overflow-hidden">
                      <div className="absolute inset-0 p-4">
                        <AIVisual index={idx} color={cap.color} />
                      </div>
                    </div>
                    <motion.div
                      animate={{ y: [0, -6, 0] }}
                      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                      className="absolute -top-3 -right-3 w-14 h-14 rounded-2xl flex items-center justify-center"
                      style={{ backgroundColor: `${cap.color}15`, border: `1px solid ${cap.color}30` }}
                    >
                      <cap.icon className="w-6 h-6" style={{ color: cap.color }} />
                    </motion.div>
                  </div>
                </AnimatedSection>

                {/* Content */}
                <AnimatedSection className={isReversed ? 'lg:order-1' : ''}>
                  <span className="text-xs font-semibold tracking-[0.2em] uppercase" style={{ color: cap.color }}>
                    {String(idx + 1).padStart(2, '0')} / {cap.shortTitle}
                  </span>
                  <h2 className="text-2xl md:text-3xl font-bold mt-3 mb-4">{cap.title}</h2>
                  <p className="text-white/50 text-sm md:text-base mb-8 leading-relaxed">{cap.description}</p>
                  <div className="space-y-3">
                    {cap.features.map((feature, i) => (
                      <motion.div key={i}
                        initial={{ opacity: 0, x: -16 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.08 }}
                        className="flex items-center gap-3"
                      >
                        <Zap className="w-4 h-4 flex-shrink-0" style={{ color: cap.color }} />
                        <span className="text-white/70 text-sm">{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </AnimatedSection>
              </div>
            </div>
          </section>
        );
      })}

      {/* How AI Works */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="text-center mb-16">
            <span className="text-[#00D4FF] text-xs font-semibold tracking-[0.2em] uppercase">How It Works</span>
            <h2 className="text-2xl md:text-3xl font-bold mt-4">AI That Learns Your Business</h2>
          </AnimatedSection>
          <AnimatedSection>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { step: '01', title: 'Data Collection', desc: 'AI learns from your historical RFQs, quotes, and outcomes' },
                { step: '02', title: 'Pattern Recognition', desc: 'Identifies winning patterns and pricing strategies' },
                { step: '03', title: 'Prediction & Analysis', desc: 'Provides real-time recommendations and risk alerts' },
                { step: '04', title: 'Continuous Learning', desc: 'Improves accuracy with every new data point' },
              ].map((item, i) => (
                <div key={item.step} className="relative">
                  <motion.div className="glass-card p-6 text-center h-full"
                    whileHover={{ y: -4 }} transition={{ duration: 0.3 }}>
                    <div className="text-4xl font-bold text-[#00D4FF]/20 mb-4">{item.step}</div>
                    <h3 className="font-semibold mb-2 text-sm">{item.title}</h3>
                    <p className="text-white/50 text-xs">{item.desc}</p>
                  </motion.div>
                  {i < 3 && (
                    <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-px bg-gradient-to-r from-[#00D4FF]/40 to-transparent" />
                  )}
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* AI Add-on CTA */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <div className="glass-card p-12 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-gradient-to-br from-[#00D4FF]/10 via-[#3B82F6]/5 to-transparent rounded-full blur-[100px]" />
              <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-4">Unlock AI BOM Automation</h2>
                  <p className="text-white/50 mb-6 text-sm">
                    Our most advanced AI module extracts BOMs from drawings, documents, and images
                    with intelligent pattern recognition and auto-mapping.
                  </p>
                  <div className="flex items-center gap-3 mb-6">
                    <span className="px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/20 to-cyan-500/20 border border-purple-500/30 text-purple-400 text-sm font-medium"
                      data-testid="ai-addon-badge">
                      AI Copilot Suite Add-on
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4">
                    {/* <Link to="/pricing"
                      className="px-8 py-4 bg-[#00D4FF] text-black font-semibold rounded-full hover:shadow-[0_0_30px_rgba(0,212,255,0.4)] transition-all flex items-center justify-center gap-2"
                      data-testid="ai-pricing-btn">
                      View Pricing <ArrowRight className="w-5 h-5" />
                    </Link> */}
                    {/* <Link to="/demo"
                      className="px-8 py-4 text-white font-medium rounded-full border border-white/20 hover:bg-white/5 transition-all text-center"
                      data-testid="ai-demo-btn">
                      See Demo
                    </Link> */}
                  </div>
                </div>
                <div className="glass-card p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Brain className="w-8 h-8 text-[#00D4FF]" />
                    <span className="font-semibold">AI BOM Automation</span>
                  </div>
                  <div className="space-y-3">
                    {['Drawing to BOM extraction', 'Intelligent data mapping', 'Auto-categorization', 'Pattern recognition', 'Multi-format support'].map((f, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-white/60">
                        <Sparkles className="w-4 h-4 text-[#00D4FF]" />
                        {f}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default AICapabilitiesPage;
